import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Zap,
  Pause,
  Play,
  SkipForward,
  Square,
  PhoneCall,
  Bot,
  PhoneOff,
  Minimize2,
  Volume2,
  VolumeX,
  User,
  Mic,
  MicOff,
  ShieldCheck,
  ShieldAlert,
  Grid3X3,
  PhoneForwarded,
  Delete,
  Briefcase,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useAppUsers } from '@/hooks/useAppUsers';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DealForm from '@/components/deals/DealForm';
import { useDeals } from '@/hooks/useDeals';
import type { DialerAttempt, DialerSession } from '@/hooks/usePowerDialer';
import { useOutboundActions } from '@/hooks/useOutboundActions';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import type { OutboundStatus } from '@/hooks/useOutboundQueue';

const outcomeLabel: Record<string, string> = {
  human: 'Humano',
  machine: 'Caixa postal',
  no_answer: 'Não atendeu',
  busy: 'Ocupado',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

const outcomeTone: Record<string, string> = {
  human: 'text-emerald-600',
  machine: 'text-amber-600',
  no_answer: 'text-muted-foreground',
  busy: 'text-orange-600',
  failed: 'text-destructive',
  cancelled: 'text-muted-foreground',
};

const statusLabel: Record<string, string> = {
  dialing: 'Discando',
  ringing: 'Chamando',
  connected: 'Em conversa',
};

const statusTone: Record<string, string> = {
  dialing: 'border-muted-foreground/30 text-muted-foreground',
  ringing: 'border-primary/40 text-primary',
  connected: 'border-emerald-500/50 text-emerald-700',
};


export function attemptName(a: DialerAttempt) {
  return [a.contact?.first_name, a.contact?.last_name].filter(Boolean).join(' ') || a.to_e164;
}

/** Tom de chamada (ringback) enquanto há linhas tocando e ninguém conectado. */
function useRingback(active: boolean, enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active || !enabled) return;

    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = ctxRef.current ?? new AudioCtx();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') void ctx.resume();

    const beep = () => {
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.08, now + 0.05);
      gain.gain.setValueAtTime(0.08, now + 1.9);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 2);
      gain.connect(ctx.destination);
      [440, 480].forEach((freq) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start(now);
        osc.stop(now + 2);
      });
    };

    beep();
    timerRef.current = setInterval(beep, 4000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [active, enabled]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      void ctxRef.current?.close();
      ctxRef.current = null;
    };
  }, []);
}

function elapsedFrom(iso?: string | null) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

function formatSeconds(total: number) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

interface PowerDialerConsoleProps {
  open: boolean;
  onMinimize: () => void;
  session: DialerSession;
  attempts: DialerAttempt[];
  liveAttempts: DialerAttempt[];
  connectedAttempt: DialerAttempt | null;
  isBusy: boolean;
  onPause: () => void;
  onResume: () => void;
  onSkip: () => void;
  onHangupConnected?: () => void;
  onHoldConnected?: () => void;
  onUnholdConnected?: () => void;
  onTransferConnected?: (targetUserId: string) => Promise<unknown> | void;
  onStartTransfer?: (targetUserId: string, mode: 'warm' | 'cold') => Promise<unknown> | void;
  onCompleteTransfer?: () => Promise<unknown> | void;
  onCancelTransfer?: () => Promise<unknown> | void;
  transferState?: {
    status: 'calling_target' | 'target_connected' | 'target_unavailable' | null;
    mode: string | null;
    targetUserId: string | null;
  } | null;
  onSendDtmf?: (digit: string) => void;
  onEnd: () => void;
  onToggleMute: () => boolean;
  isAudioReady: boolean;
}

export function PowerDialerConsole({
  open,
  onMinimize,
  session,
  attempts,
  liveAttempts,
  connectedAttempt,
  isBusy,
  onPause,
  onResume,
  onSkip,
  onHangupConnected,
  onHoldConnected,
  onUnholdConnected,
  onTransferConnected,
  onStartTransfer,
  onCompleteTransfer,
  onCancelTransfer,
  transferState,
  onSendDtmf,
  onEnd,
  onToggleMute,
  isAudioReady,
}: PowerDialerConsoleProps) {
  const [soundOn, setSoundOn] = useState(true);
  const [tick, setTick] = useState(0);
  const [disposed, setDisposed] = useState<Record<string, true>>({});
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dtmfDigits, setDtmfDigits] = useState('');
  const [transferSearch, setTransferSearch] = useState('');
  const [transferTargetName, setTransferTargetName] = useState<string | null>(null);
  const [dealAttempt, setDealAttempt] = useState<DialerAttempt | null>(null);
  const [forceNewDeal, setForceNewDeal] = useState(false);
  const [isSavingDeal, setIsSavingDeal] = useState(false);
  const { users: appUsers = [] } = useAppUsers();
  const { data: currentUser } = useCurrentAppUser();
  const { updateStatus, flagAmdFalsePositive, reclassifyAttempt } = useOutboundActions();
  const { createDeal } = useDeals({ enabled: false });

  // Negociação ativa (não won/lost) do contato da tentativa selecionada
  const dealContactId = dealAttempt?.contact_id ?? null;
  const { data: activeDeal, isLoading: isLoadingActiveDeal } = useQuery({
    queryKey: ['power-dialer-active-deal', dealContactId],
    enabled: !!dealContactId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('id, name, stage')
        .eq('contact_id', dealContactId as string)
        .not('stage', 'in', '("won","lost")')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });


  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    setIsOnHold(false);
    setDtmfDigits('');
  }, [connectedAttempt?.id]);

  const pressDigit = (digit: string) => {
    onSendDtmf?.(digit);
    setDtmfDigits((prev) => (prev + digit).slice(-20));
  };

  const handleHoldToggle = async () => {
    const next = !isOnHold;
    setIsOnHold(next);
    try {
      if (next) await onHoldConnected?.();
      else await onUnholdConnected?.();
    } catch {
      setIsOnHold(!next);
    }
  };

  const handleTransfer = async (userId: string, name: string, mode: 'warm' | 'cold') => {
    setTransferTargetName(name);
    if (onStartTransfer) await onStartTransfer(userId, mode);
    else await onTransferConnected?.(userId);
    toast.success(
      mode === 'cold'
        ? `Cliente transferido para ${name}.`
        : `Chamando ${name}. O cliente está em espera.`,
    );
  };

  const transferTargets = useMemo(
    () =>
      appUsers
        .filter((u) => u.active && u.id !== currentUser?.id)
        .filter((u) =>
          transferSearch
            ? `${u.display_name ?? ''} ${u.email ?? ''}`.toLowerCase().includes(transferSearch.toLowerCase())
            : true,
        )
        .slice(0, 30),
    [appUsers, transferSearch, currentUser?.id],
  );

  const isActive = session.status === 'active';
  const ringing = liveAttempts.length > 0 && !connectedAttempt && isActive;
  useRingback(ringing, soundOn && open);

  // Contato atendido por humano cuja ligação já terminou e ainda não teve desfecho registrado
  const wrapUp = useMemo(
    () =>
      attempts.find(
        (a) =>
          a.outcome === 'human' &&
          a.status === 'completed' &&
          !!a.assignment_id &&
          !disposed[a.id],
      ) ?? null,
    [attempts, disposed],
  );

  const dispose = (attempt: DialerAttempt, status: OutboundStatus) => {
    if (!attempt.assignment_id || !currentUser?.id) return;
    setDisposed((prev) => ({ ...prev, [attempt.id]: true }));
    updateStatus.mutate({
      assignmentId: attempt.assignment_id,
      sdrId: currentUser.id,
      status,
      incrementAttempts: true,
    });
  };

  const openDealForm = (attempt: DialerAttempt) => {
    if (!attempt.contact_id) {
      toast.error('Tentativa sem contato vinculado');
      return;
    }
    setForceNewDeal(false);
    setDealAttempt(attempt);
  };

  const closeDealForm = () => {
    setDealAttempt(null);
    setForceNewDeal(false);
  };

  const linkExistingDeal = () => {
    if (dealAttempt) dispose(dealAttempt, 'deal_created');
    toast.success('Contato vinculado à negociação existente');
    closeDealForm();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDealSubmit = async (dealData: any) => {
    if (!dealAttempt?.contact_id) return;
    setIsSavingDeal(true);
    try {
      await createDeal({ ...dealData, contact_id: dealAttempt.contact_id });
      dispose(dealAttempt, 'deal_created');
      toast.success('Negociação criada com sucesso!');
      closeDealForm();
    } catch (err) {
      console.error('[PowerDialer] Create deal error:', err);
      toast.error('Erro ao criar negociação');
    } finally {
      setIsSavingDeal(false);
    }
  };



  const markAmdError = (attempt: DialerAttempt, hangUp = false) => {
    if (!currentUser?.id) return;
    setDisposed((prev) => ({ ...prev, [attempt.id]: true }));
    flagAmdFalsePositive.mutate({
      attemptId: attempt.id,
      assignmentId: attempt.assignment_id,
      sdrId: currentUser.id,
    });
    // Se a ligação ainda está em curso, derruba a linha para o discador seguir
    if (hangUp) (onHangupConnected ?? onSkip)();
  };

  const reclassify = (
    attempt: DialerAttempt,
    outcome: 'human' | 'machine' | 'no_answer' | 'busy' | 'failed',
  ) => {
    if (!currentUser?.id) return;
    reclassifyAttempt.mutate(
      {
        attemptId: attempt.id,
        assignmentId: attempt.assignment_id,
        sdrId: currentUser.id,
        outcome,
      },
      {
        onSuccess: () => toast.success(`Desfecho corrigido para "${outcomeLabel[outcome]}"`),
        onError: () => toast.error('Não foi possível corrigir o desfecho'),
      },
    );
    setDisposed((prev) => {
      const next = { ...prev };
      if (outcome === 'human') delete next[attempt.id];
      else next[attempt.id] = true;
      return next;
    });

  };




  const completed = useMemo(() => attempts.filter((a) => a.status === 'completed').slice(0, 12), [attempts]);
  const remaining = Math.max(0, (session.queue_contact_ids?.length ?? 0) - session.total_dialed);
  const progress = session.queue_contact_ids?.length
    ? Math.min(100, Math.round((session.total_dialed / session.queue_contact_ids.length) * 100))
    : 0;

  // referenced so the per-second re-render is not optimized away
  void tick;

  return (
    <>
    <Dialog open={open}>

      <DialogContent
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden border-0 shadow-2xl [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <VisuallyHidden>
          <DialogTitle>Power Dialer</DialogTitle>
          <DialogDescription>Painel do discador automático em andamento</DialogDescription>
        </VisuallyHidden>

        {/* Cabeçalho */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/15">
              <Zap className="h-4.5 w-4.5" />
              {isActive && (
                <span className="absolute inset-0 rounded-full border border-primary-foreground/40 animate-ping" />
              )}
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold">Power Dialer</p>
              <p className="text-xs opacity-80">
                {!isAudioReady
                  ? 'Conectando áudio do SDR...'
                  : connectedAttempt
                  ? 'Conectado com um humano'
                  : isActive
                    ? ringing
                      ? 'Chamando contatos...'
                      : 'Aguardando linhas'
                    : 'Pausado'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/15"
              onClick={() => setSoundOn((v) => !v)}
              title={soundOn ? 'Silenciar tom de chamada' : 'Ativar tom de chamada'}
            >
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/15"
              onClick={onMinimize}
              title="Minimizar"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className={`mx-5 mt-4 flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
          isAudioReady ? 'border-emerald-200 bg-emerald-50' : 'border-destructive/30 bg-destructive/5'
        }`}>
          <div className="flex min-w-0 items-center gap-2">
            {isAudioReady ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className="h-4 w-4 text-destructive" />}
            <span className="text-xs font-medium">
              {isAudioReady ? 'Microfone e conferência conectados' : 'Áudio ainda não confirmado — discagem bloqueada'}
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-8 shrink-0 gap-1.5"
            disabled={!isAudioReady}
            onClick={() => setIsMuted(onToggleMute())}
          >
            {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            {isMuted ? 'Ativar microfone' : 'Silenciar'}
          </Button>
        </div>

        {/* Conectado */}
        {connectedAttempt && (
          <div className="mx-5 mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-white">
              <PhoneCall className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-emerald-900 truncate">{attemptName(connectedAttempt)}</p>
              <p className="text-xs text-emerald-700">{connectedAttempt.to_e164}</p>
              {connectedAttempt.amd_reason === 'deteccao_incerta' && (
                <p className="text-[11px] text-amber-700">Detecção incerta — confirme se é humano</p>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="mt-1 h-7 px-2 text-[11px] text-amber-700 hover:text-amber-800"
                disabled={flagAmdFalsePositive.isPending}
                onClick={() => markAmdError(connectedAttempt, true)}
                title="Não era humano (caixa postal/gravação) — encerra a ligação e devolve o contato para a fila"
              >
                <Bot className="h-3.5 w-3.5 mr-1.5" /> Não era humano (erro do AMD)
              </Button>
            </div>
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white rounded-full">
              {formatSeconds(elapsedFrom(connectedAttempt.connected_at))}
            </Badge>

          </div>
        )}

        {/* Controles da chamada em andamento */}
        {connectedAttempt && (
          <div className="mx-5 mt-2 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              onClick={() => setIsMuted(onToggleMute())}
            >
              {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              {isMuted ? 'Ativar mic' : 'Mudo'}
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1.5">
                  <Grid3X3 className="h-3.5 w-3.5" /> Teclado
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex-1 truncate rounded-md border bg-muted/40 px-2 py-1 text-sm font-mono">
                    {dtmfDigits || '—'}
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDtmfDigits('')}>
                    <Delete className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                    <Button
                      key={digit}
                      variant="outline"
                      className="h-10 text-base font-medium"
                      onClick={() => pressDigit(digit)}
                    >
                      {digit}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Button
              size="sm"
              variant={isOnHold ? 'default' : 'outline'}
              className="h-8 gap-1.5"
              disabled={isBusy}
              onClick={handleHoldToggle}
            >
              {isOnHold ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              {isOnHold ? 'Retomar' : 'Espera'}
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={isBusy || !!transferState?.status}>
                  <PhoneForwarded className="h-3.5 w-3.5" /> Transferir
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-2" align="start">
                <Input
                  placeholder="Buscar usuário..."
                  value={transferSearch}
                  onChange={(e) => setTransferSearch(e.target.value)}
                  className="mb-2 h-8"
                />
                <ScrollArea className="h-56">
                  <div className="space-y-1 pr-2">
                    {transferTargets.length === 0 && (
                      <p className="px-2 py-4 text-center text-xs text-muted-foreground">Nenhum usuário encontrado</p>
                    )}
                    {transferTargets.map((u) => (
                      <div
                        key={u.id}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                      >
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">{u.display_name || u.email}</span>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-6 px-2 text-[11px]"
                          disabled={isBusy}
                          onClick={() => handleTransfer(u.id, u.display_name || u.email || 'usuário', 'warm')}
                        >
                          Anunciada
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[11px]"
                          disabled={isBusy}
                          onClick={() => handleTransfer(u.id, u.display_name || u.email || 'usuário', 'cold')}
                        >
                          Direta
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <p className="px-2 pt-2 text-[11px] text-muted-foreground">
                  Anunciada: cliente fica em espera enquanto você fala com o colega. Direta: cliente vai na hora.
                </p>
              </PopoverContent>
            </Popover>

            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              disabled={isBusy || !connectedAttempt.contact_id}
              onClick={() => openDealForm(connectedAttempt)}
            >
              <Briefcase className="h-3.5 w-3.5" /> Criar negociação
            </Button>

            <Button
              size="sm"
              variant="destructive"
              className="h-8 gap-1.5 ml-auto"
              disabled={isBusy}
              onClick={() => onHangupConnected?.()}
            >
              <PhoneOff className="h-3.5 w-3.5" /> Desligar
            </Button>

          </div>
        )}

        {/* Transferência anunciada em andamento */}
        {connectedAttempt && transferState?.status && (
          <div className="mx-5 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">
              {transferState.status === 'calling_target' &&
                `Chamando ${transferTargetName ?? 'o colega'}... cliente em espera`}
              {transferState.status === 'target_connected' &&
                `${transferTargetName ?? 'Colega'} na linha — cliente continua em espera`}
              {transferState.status === 'target_unavailable' &&
                `${transferTargetName ?? 'Colega'} não atendeu`}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                className="h-8"
                disabled={isBusy || transferState.status !== 'target_connected'}
                onClick={() => onCompleteTransfer?.()}
              >
                Concluir transferência
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={isBusy}
                onClick={() => onCancelTransfer?.()}
              >
                Cancelar e voltar ao cliente
              </Button>
            </div>
          </div>
        )}




        {/* Registro de resultado (wrap-up) do contato atendido */}
        {wrapUp && (
          <div className="mx-5 mt-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
            <p className="text-sm font-semibold truncate">Resultado de {attemptName(wrapUp)}</p>
            <p className="text-xs text-muted-foreground mb-2.5">
              Registre o desfecho para atualizar a fila de prospecção.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={updateStatus.isPending}
                onClick={() => dispose(wrapUp, 'called_no_answer')}
              >
                Ligar depois
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={updateStatus.isPending}
                onClick={() => dispose(wrapUp, 'called_not_interested')}
              >
                Não interessado
              </Button>
              <Button
                size="sm"
                className="h-8 gap-1.5"
                disabled={updateStatus.isPending}
                onClick={() => openDealForm(wrapUp)}
              >
                <Briefcase className="h-3.5 w-3.5" /> Criar negociação
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                disabled={updateStatus.isPending}
                onClick={() => dispose(wrapUp, 'deal_created')}
                title="Já criei a negociação em outro lugar — só marcar o desfecho"
              >
                Marcar sem criar
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={reclassifyAttempt.isPending}
                onClick={() => reclassify(wrapUp, 'no_answer')}
                title="Chamou mas ninguém falou — registra como não atendeu"
              >
                <PhoneOff className="h-3.5 w-3.5 mr-1.5" /> Não atendeu
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-amber-700 hover:text-amber-800"
                disabled={flagAmdFalsePositive.isPending}
                onClick={() => markAmdError(wrapUp)}
                title="Não era humano — devolve o contato para a fila"
              >
                <Bot className="h-3.5 w-3.5 mr-1.5" /> Caixa postal
              </Button>
            </div>


          </div>
        )}

        {/* Linhas ao vivo */}
        <div className="px-5 pt-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Linhas ativas ({liveAttempts.length}/{session.concurrency})
          </p>
          <div className="space-y-2">
            {liveAttempts.length === 0 && (
              <div className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                Nenhuma linha discando no momento
              </div>
            )}
            {liveAttempts.map((a) => {
              const isConnected = a.status === 'connected';
              return (
                <div
                  key={a.id}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
                    isConnected ? 'border-emerald-200 bg-emerald-50/60' : 'bg-muted/40'
                  }`}
                >
                  <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-background border">
                    <User className="h-4 w-4 text-muted-foreground" />
                    {!isConnected && (
                      <span className="absolute inset-0 rounded-full border-2 border-primary/50 animate-ping" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{attemptName(a)}</p>
                    <p className="text-xs text-muted-foreground">{a.to_e164}</p>
                  </div>
                  <Badge variant="outline" className={`rounded-full text-[11px] ${statusTone[a.status] ?? ''}`}>
                    {statusLabel[a.status] ?? a.status} ·{' '}
                    {formatSeconds(elapsedFrom(isConnected ? a.connected_at : a.created_at))}
                  </Badge>

                </div>
              );
            })}
          </div>
        </div>

        {/* Métricas */}
        <div className="px-5 pt-4 grid grid-cols-4 gap-2 text-center">
          {[
            { label: 'Discadas', value: session.total_dialed, tone: '' },
            { label: 'Humanos', value: session.total_human, tone: 'text-emerald-600' },
            { label: 'Caixa postal', value: session.total_machine, tone: 'text-amber-600' },
            { label: 'Restantes', value: remaining, tone: 'text-muted-foreground' },
          ].map((m) => (
            <div key={m.label} className="rounded-lg border bg-background py-2">
              <p className={`text-lg font-semibold ${m.tone}`}>{m.value}</p>
              <p className="text-[11px] text-muted-foreground">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="px-5 pt-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Histórico */}
        <div className="px-5 pt-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Últimas tentativas</p>
          <ScrollArea className="h-[140px] rounded-lg border">
            <div className="divide-y">
              {completed.length === 0 && (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">Ainda sem resultados</p>
              )}
              {completed.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                  <span className="truncate">{attemptName(a)}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {!a.amd_false_positive && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-[11px]"
                            disabled={reclassifyAttempt.isPending}
                            title="Corrigir o desfecho registrado automaticamente"
                          >
                            Corrigir
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-52 p-1">
                          <p className="px-2 py-1 text-[11px] text-muted-foreground">
                            Qual foi o desfecho real?
                          </p>
                          {([
                            ['human', 'Atendeu (humano)'],
                            ['no_answer', 'Não atendeu'],
                            ['machine', 'Caixa postal'],
                            ['busy', 'Ocupado'],
                            ['failed', 'Falhou / número inválido'],
                          ] as const).map(([value, label]) => (
                            <button
                              key={value}
                              className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted ${
                                a.outcome === value ? 'font-semibold' : ''
                              }`}
                              onClick={() => reclassify(a, value)}
                            >
                              {label}
                              {a.outcome === value && <span className="text-[10px] text-muted-foreground">atual</span>}
                            </button>
                          ))}
                        </PopoverContent>
                      </Popover>
                    )}
                    {a.amd_false_positive && (
                      <span className="text-[11px] text-amber-700">Erro do AMD</span>
                    )}

                    <span className={`flex items-center gap-1 ${outcomeTone[a.outcome ?? ''] ?? 'text-muted-foreground'}`}>
                      {a.outcome === 'human' ? <PhoneCall className="h-3 w-3" /> : a.outcome === 'machine' ? <Bot className="h-3 w-3" /> : <PhoneOff className="h-3 w-3" />}
                      {outcomeLabel[a.outcome ?? ''] ?? '—'}
                    </span>
                  </div>
                </div>
              ))}

            </div>
          </ScrollArea>
        </div>

        {/* Controles */}
        <div className="mt-4 flex items-center justify-between gap-2 border-t bg-muted/30 px-5 py-3">
          <div className="flex items-center gap-2">
            {isActive ? (
              <>
                <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={isBusy || !!connectedAttempt} onClick={onSkip}>
                  <SkipForward className="h-3.5 w-3.5" /> Pular
                </Button>
                <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={isBusy || !!connectedAttempt} onClick={onPause}>
                  <Pause className="h-3.5 w-3.5" /> Pausar
                </Button>
              </>
            ) : (
              <Button size="sm" className="h-8 gap-1.5" disabled={isBusy} onClick={onResume}>
                <Play className="h-3.5 w-3.5" /> Retomar
              </Button>
            )}
          </div>
          <Button size="sm" variant="destructive" className="h-8 gap-1.5" disabled={isBusy} onClick={onEnd}>
            <Square className="h-3.5 w-3.5" /> Encerrar sessão
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Criação/vínculo de negociação sem sair da discagem */}
    <Dialog open={!!dealAttempt} onOpenChange={(v) => !v && closeDealForm()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogTitle>
          {dealAttempt ? `Criar negociação — ${attemptName(dealAttempt)}` : 'Criar negociação'}
        </DialogTitle>
        <DialogDescription>
          Registre a oportunidade sem interromper a sessão de discagem.
        </DialogDescription>

        {isLoadingActiveDeal ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Verificando negociações existentes...</p>
        ) : activeDeal && !forceNewDeal ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              Este contato já possui uma negociação ativa:{' '}
              <strong>{activeDeal.name || 'sem nome'}</strong> (etapa: {activeDeal.stage}).
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={linkExistingDeal} disabled={updateStatus.isPending}>
                Vincular a esta
              </Button>
              <Button size="sm" variant="outline" onClick={() => setForceNewDeal(true)}>
                Criar outra assim mesmo
              </Button>
              <Button size="sm" variant="ghost" onClick={closeDealForm}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <DealForm
            initialContactId={dealAttempt?.contact_id ?? undefined}
            isProcessing={isSavingDeal}
            onSubmit={handleDealSubmit}
            onCancel={closeDealForm}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}


interface DockProps {
  onOpen: () => void;
  liveCount: number;
  connectedName: string | null;
}

export function PowerDialerDock({ onOpen, liveCount, connectedName }: DockProps) {
  return (
    <button
      onClick={onOpen}
      className="fixed bottom-5 right-5 z-50 flex items-center gap-2.5 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-xl hover:opacity-90 transition"
    >
      <span className="relative flex h-5 w-5 items-center justify-center">
        <Zap className="h-4 w-4" />
        <span className="absolute inset-0 rounded-full border border-primary-foreground/50 animate-ping" />
      </span>
      <span className="text-sm font-medium">
        {connectedName ? `Em conversa: ${connectedName}` : `Power Dialer · ${liveCount} linha(s)`}
      </span>
    </button>
  );
}
